import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  MagnifyingGlass,
  MapPin,
  Clock,
  Phone,
  Envelope,
  User,
  CheckCircle,
  Lock,
  ShoppingCart,
  CurrencyDollar,
  Funnel,
  ArrowLeft,
  Eye,
  X,
  Lightning,
  Fire,
  House,
  Drop,
  PaintBrush,
  Thermometer,
  Hammer,
  Wall,
  Buildings,
  HardHat,
  Star,
  ShieldCheck,
  Sparkle,
  CaretDown,
  Receipt,
  Warning,
  CreditCard,
  Coins,
  Plus,
  Wallet,
  Gift,
  ArrowRight,
  TrendUp,
  Bell,
  BellRinging,
  Gear,
  Toggle,
  Check,
  DeviceMobile
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import axiosInstance from '../api/api';
import { openCheckout } from '../utils/checkout';

const jobTypeIcons = {
  bathroom_remodel: Drop,
  kitchen_remodel: House,
  roofing: House,
  painting: PaintBrush,
  flooring: Wall,
  electrical: Lightning,
  plumbing: Drop,
  hvac: Thermometer,
  carpentry: Hammer,
  concrete: Buildings,
  deck_patio: House,
  windows_doors: Wall,
  siding: House,
  landscaping: House,
  general: HardHat,
  other: Hammer
};

const jobTypeLabels = {
  bathroom_remodel: 'Bathroom Remodel',
  kitchen_remodel: 'Kitchen Remodel',
  roofing: 'Roofing',
  painting: 'Painting',
  flooring: 'Flooring',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  carpentry: 'Carpentry',
  concrete: 'Concrete/Masonry',
  deck_patio: 'Deck/Patio',
  windows_doors: 'Windows & Doors',
  siding: 'Siding',
  landscaping: 'Landscaping',
  general: 'General Contractor',
  other: 'Other'
};

const timelineLabels = {
  asap: 'ASAP / Urgent',
  '1-2_weeks': 'Within 1-2 weeks',
  '1_month': 'Within a month',
  flexible: 'Flexible'
};

export default function LeadsMarketplace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [purchasedLeads, setPurchasedLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPurchasedTab, setShowPurchasedTab] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [filterJobType, setFilterJobType] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterZip, setFilterZip] = useState('');
  const [filterTimeline, setFilterTimeline] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const limit = 12;
  
  // Credits state
  const [creditBalance, setCreditBalance] = useState(0);
  const [referralCredits, setReferralCredits] = useState(0);
  const [useReferralCredits, setUseReferralCredits] = useState(true);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditPackages, setCreditPackages] = useState({});
  const [purchasingCredits, setPurchasingCredits] = useState(false);
  const [verifyingCredits, setVerifyingCredits] = useState(false);

  // Lead Alerts state
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [alertPrefs, setAlertPrefs] = useState({
    enabled: false,
    job_types: [],
    states: [],
    zip_codes: [],
    timelines: [],
    frequency: 'instant',
    notification_channels: ['in_app', 'email']
  });
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [autoTopup, setAutoTopup] = useState(null);
  const [atThreshold, setAtThreshold] = useState(10);
  const [atPackage, setAtPackage] = useState('starter');
  const [atSaving, setAtSaving] = useState(false);

  // Handle Stripe redirect on mount
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const creditsStatus = searchParams.get('credits');
    const sessionId = searchParams.get('session_id');
    const leadId = searchParams.get('lead_id');
    const packageId = searchParams.get('package');

    if (paymentStatus === 'success' && sessionId) {
      verifyPayment(sessionId, leadId);
      setSearchParams({});
    } else if (paymentStatus === 'cancelled') {
      toast({
        title: "Payment Cancelled",
        description: "Your lead purchase was cancelled. You were not charged.",
        variant: "destructive"
      });
      setSearchParams({});
    } else if (creditsStatus === 'success' && sessionId) {
      verifyCreditPurchase(sessionId);
      setSearchParams({});
    } else if (creditsStatus === 'cancelled') {
      toast({
        title: "Purchase Cancelled",
        description: "Credit purchase was cancelled. You were not charged.",
        variant: "destructive"
      });
      setSearchParams({});
    } else if (searchParams.get('autotopup') === 'success' && sessionId) {
      verifyAutoTopupSetup(sessionId);
      setSearchParams({});
    } else if (searchParams.get('autotopup') === 'cancelled') {
      toast({
        title: "Auto Top-Up Cancelled",
        description: "No card was saved. You were not charged.",
        variant: "destructive"
      });
      setSearchParams({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [searchParams]);

  useEffect(() => {
    checkAuthorization();
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      if (showPurchasedTab) {
        fetchPurchasedLeads();
      } else {
        fetchLeads();
      }
      fetchCreditBalance();
      fetchCreditPackages();
      fetchAutoTopup();
      fetchAlertPreferences();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [isAuthorized, currentPage, filterJobType, filterState, filterZip, filterTimeline, showPurchasedTab]);

  const checkAuthorization = async () => {
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!userStr || !token) {
      setAuthChecking(false);
      setIsAuthorized(false);
      return;
    }
    
    try {
      // First check localStorage
      const localUser = JSON.parse(userStr);
      
      // Verify with server to ensure user_type is current
      try {
        const response = await axiosInstance.get('/auth/me');
        const userData = response.data?.user || response.data;
        if (userData && userData.user_type === 'contractor') {
          localStorage.setItem('user', JSON.stringify(userData));
          setIsAuthorized(true);
        } else if (userData) {
          localStorage.setItem('user', JSON.stringify(userData));
          setIsAuthorized(false);
        }
      } catch (serverError) {
        // If server check fails, fall back to localStorage
        if (localUser.user_type === 'contractor') {
          setIsAuthorized(true);
        }
      }
    } catch (e) {
      console.error('Error parsing user:', e);
    }
    setAuthChecking(false);
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', currentPage * limit);
      params.append('limit', limit);
      if (filterJobType) params.append('job_type', filterJobType);
      if (filterState) params.append('state', filterState);
      if (filterZip) params.append('zip_code', filterZip);
      if (filterTimeline) params.append('timeline', filterTimeline);

      const response = await axiosInstance.get(`/leads/marketplace?${params.toString()}`);
      if (response.data.success) {
        setLeads(response.data.leads);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (error.response?.status === 401) {
        toast({ title: "Session Expired", description: "Please log in again to access leads.", variant: "destructive" });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else if (error.response?.status === 403) {
        // User type mismatch - refresh from server or redirect
        toast({ title: "Access Denied", description: "Only contractors can access the Leads Marketplace.", variant: "destructive" });
        // Refresh user data from server
        try {
          const profileRes = await axiosInstance.get('/auth/me');
          if (profileRes.data) {
            localStorage.setItem('user', JSON.stringify(profileRes.data));
            if (profileRes.data.user_type !== 'contractor') {
              navigate('/worker-dashboard');
            }
          }
        } catch (profileError) {
          // If profile fetch fails, just redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        }
      } else {
        toast({ title: "Error", description: "Failed to fetch leads. Please try again.", variant: "destructive" });
      }
    }
    setLoading(false);
  };

  const fetchPurchasedLeads = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/leads/marketplace/purchased');
      if (response.data.success) {
        setPurchasedLeads(response.data.purchases);
      }
    } catch (error) {
      console.error('Error fetching purchased leads:', error);
    }
    setLoading(false);
  };

  const fetchCreditBalance = async () => {
    try {
      const response = await axiosInstance.get('/credits/balance');
      if (response.data.success) {
        setCreditBalance(response.data.balance);
      }
    } catch (error) {
      console.error('Error fetching credit balance:', error);
    }
    // Also fetch referral credits so we can show the apply-referral-credits toggle
    try {
      const r = await axiosInstance.get('/referrals/me');
      setReferralCredits(r.data?.referral_credits || 0);
    } catch (error) {
      // Silent — not all users have referral data yet
    }
  };

  const fetchCreditPackages = async () => {
    try {
      const response = await axiosInstance.get('/credits/packages');
      if (response.data.success) {
        setCreditPackages(response.data.packages);
      }
    } catch (error) {
      console.error('Error fetching credit packages:', error);
    }
  };

  const fetchAutoTopup = async () => {
    try {
      const r = await axiosInstance.get('/credits/auto-topup');
      if (r.data.success && r.data.settings) {
        setAutoTopup(r.data.settings);
        setAtThreshold(r.data.settings.threshold || 10);
        setAtPackage(r.data.settings.package_id || 'starter');
      }
    } catch (e) {
      // silent — feature not yet configured
    }
  };

  const startAutoTopupSetup = async () => {
    setAtSaving(true);
    try {
      const r = await axiosInstance.post('/credits/auto-topup/setup', {
        threshold: atThreshold,
        package_id: atPackage,
        origin_url: window.location.origin,
      });
      if (r.data.success && r.data.checkout_url) {
        openCheckout(r.data.checkout_url);
        return;
      }
      throw new Error('No checkout URL');
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not start card setup', variant: 'destructive' });
      setAtSaving(false);
    }
  };

  const verifyAutoTopupSetup = async (sessionId) => {
    try {
      const r = await axiosInstance.post(`/credits/auto-topup/verify?session_id=${sessionId}`);
      if (r.data.success) {
        setAutoTopup(r.data.settings);
        setShowCreditsModal(true);
        toast({ title: 'Auto Top-Up Enabled', description: 'Card saved. Credits will refill automatically when you run low.' });
      } else {
        toast({ title: 'Setup Incomplete', description: r.data.message || 'Card was not saved.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Could not verify card setup.', variant: 'destructive' });
    }
  };

  const enableSmartRefill = async () => {
    setAtSaving(true);
    try {
      const r = await axiosInstance.post('/credits/auto-topup/enable', { threshold: atThreshold, package_id: atPackage });
      if (r.data.success) {
        setAutoTopup(r.data.settings);
        toast({ title: 'Smart Refill Enabled', description: "We'll email you a one-click refill link whenever you run low on credits." });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not enable Smart Refill', variant: 'destructive' });
    }
    setAtSaving(false);
  };

  const updateAutoTopup = async (updates) => {
    setAtSaving(true);
    try {
      const r = await axiosInstance.put('/credits/auto-topup', updates);
      if (r.data.success) {
        setAutoTopup(r.data.settings);
        toast({ title: 'Auto Top-Up Updated' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Update failed', variant: 'destructive' });
    }
    setAtSaving(false);
  };

  const removeAutoTopup = async () => {
    setAtSaving(true);
    try {
      await axiosInstance.delete('/credits/auto-topup');
      setAutoTopup(prev => ({ ...(prev || {}), enabled: false, has_card: false, card_last4: null, card_brand: null }));
      toast({ title: 'Auto Top-Up Removed', description: 'Your saved card was removed.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not remove auto top-up', variant: 'destructive' });
    }
    setAtSaving(false);
  };

  const fetchAlertPreferences = async () => {
    setAlertsLoading(true);
    try {
      const response = await axiosInstance.get('/lead-alerts/preferences');
      if (response.data.success) {
        setAlertPrefs(response.data.preferences);
      }
    } catch (error) {
      console.error('Error fetching alert preferences:', error);
    }
    setAlertsLoading(false);
  };

  const saveAlertPreferences = async () => {
    setSavingAlerts(true);
    try {
      const response = await axiosInstance.post('/lead-alerts/preferences', alertPrefs);
      if (response.data.success) {
        toast({
          title: alertPrefs.enabled ? "Alerts Enabled!" : "Alerts Updated",
          description: alertPrefs.enabled 
            ? "You'll be notified when matching leads are posted."
            : "Your alert preferences have been saved.",
        });
      }
    } catch (error) {
      console.error('Error saving alert preferences:', error);
      toast({ title: "Error", description: "Failed to save alert preferences.", variant: "destructive" });
    }
    setSavingAlerts(false);
  };

  const toggleJobTypeAlert = (jobType) => {
    setAlertPrefs(prev => ({
      ...prev,
      job_types: prev.job_types.includes(jobType)
        ? prev.job_types.filter(j => j !== jobType)
        : [...prev.job_types, jobType]
    }));
  };

  const saveCurrentSearchAsAlert = () => {
    setAlertPrefs((p) => ({
      ...p,
      enabled: true,
      job_types: filterJobType ? Array.from(new Set([...p.job_types, filterJobType])) : p.job_types,
      states: filterState && filterState.length === 2 ? Array.from(new Set([...p.states, filterState])) : p.states,
      zip_codes: filterZip ? Array.from(new Set([...p.zip_codes, filterZip])) : p.zip_codes,
      timelines: filterTimeline ? Array.from(new Set([...p.timelines, filterTimeline])) : p.timelines,
    }));
    setShowAlertsModal(true);
    toast({
      title: 'Search added to your alerts',
      description: 'Pick a frequency below and save to get notified about matching leads.',
    });
  };


  const toggleStateAlert = (state) => {
    setAlertPrefs(prev => ({
      ...prev,
      states: prev.states.includes(state)
        ? prev.states.filter(s => s !== state)
        : [...prev.states, state]
    }));
  };

  const toggleTimelineAlert = (timeline) => {
    setAlertPrefs(prev => ({
      ...prev,
      timelines: prev.timelines.includes(timeline)
        ? prev.timelines.filter(t => t !== timeline)
        : [...prev.timelines, timeline]
    }));
  };

  const toggleChannelAlert = (channel) => {
    setAlertPrefs(prev => ({
      ...prev,
      notification_channels: prev.notification_channels.includes(channel)
        ? prev.notification_channels.filter(c => c !== channel)
        : [...prev.notification_channels, channel]
    }));
  };

  const verifyCreditPurchase = async (sessionId) => {
    setVerifyingCredits(true);
    try {
      const response = await axiosInstance.post(`/credits/purchase/verify?session_id=${sessionId}`);
      if (response.data.success) {
        toast({
          title: "Credits Added!",
          description: response.data.already_completed 
            ? `You already have these credits. Balance: ${response.data.new_balance}`
            : `+${response.data.credits_added} credits added. New balance: ${response.data.new_balance}`,
        });
        setCreditBalance(response.data.new_balance);
      } else {
        toast({
          title: "Verification Failed",
          description: response.data.message || "Please contact support.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error verifying credit purchase:', error);
      toast({ title: "Error", description: "Failed to verify credit purchase.", variant: "destructive" });
    }
    setVerifyingCredits(false);
  };

  const purchaseCredits = async (packageId) => {
    setPurchasingCredits(true);
    try {
      const response = await axiosInstance.post('/credits/purchase/checkout', { package_id: packageId });
      if (response.data.success && response.data.checkout_url) {
        openCheckout(response.data.checkout_url);
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to initiate checkout";
      toast({ title: "Error", description: message, variant: "destructive" });
      setPurchasingCredits(false);
    }
  };

  const purchaseLeadWithCredits = async (leadId, price) => {
    if (creditBalance < price) {
      toast({
        title: "Insufficient Credits",
        description: `You need ${price} credits but have ${creditBalance}. Buy more credits!`,
        variant: "destructive"
      });
      setShowCreditsModal(true);
      return;
    }
    
    setPurchasing(true);
    try {
      const applyReferral = useReferralCredits && referralCredits > 0;
      const response = await axiosInstance.post('/leads/marketplace/purchase-with-credits', {
        lead_id: leadId,
        apply_referral_credits: applyReferral,
      });
      if (response.data.success) {
        const refUsed = response.data.referral_credits_applied || 0;
        const creditsUsed = response.data.credits_used || 0;
        toast({
          title: "Lead Unlocked!",
          description: refUsed > 0
            ? `Applied $${refUsed} referral + ${creditsUsed} lead credits. Balance: ${response.data.new_balance}`
            : `Used ${creditsUsed} credits. Balance: ${response.data.new_balance}`,
        });
        setCreditBalance(response.data.new_balance);
        if (refUsed > 0) setReferralCredits(c => Math.max(0, c - refUsed));
        // Update the lead in the list
        setLeads(leads.map(l => l._id === leadId ? { ...l, is_purchased: true, contact: response.data.contact } : l));
        // Update selected lead if viewing
        if (selectedLead && selectedLead._id === leadId) {
          setSelectedLead({ ...selectedLead, is_purchased: true, contact: response.data.contact });
        }
        fetchPurchasedLeads();
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to purchase lead";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
    setPurchasing(false);
  };

  const verifyPayment = async (sessionId, leadId) => {
    setVerifyingPayment(true);
    try {
      const response = await axiosInstance.post(`/leads/marketplace/verify-payment?session_id=${sessionId}`);
      if (response.data.success) {
        toast({
          title: "Lead Purchased!",
          description: response.data.already_completed 
            ? "You already own this lead. Contact info is available."
            : `Contact info unlocked. You paid $${response.data.price}.`,
        });
        // Refresh leads to show updated status
        fetchLeads();
        fetchPurchasedLeads();
      } else {
        toast({
          title: "Payment Verification Failed",
          description: response.data.message || "Please contact support if you were charged.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      const message = error.response?.data?.detail || "Failed to verify payment. Please contact support.";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
    setVerifyingPayment(false);
  };

  const purchaseLead = async (leadId) => {
    setPurchasing(true);
    try {
      // Create Stripe checkout session
      const response = await axiosInstance.post('/leads/marketplace/checkout', { lead_id: leadId });
      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe Checkout
        openCheckout(response.data.checkout_url);
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to initiate checkout";
      toast({ title: "Error", description: message, variant: "destructive" });
      setPurchasing(false);
    }
    // Note: Don't set setPurchasing(false) on success because we're redirecting
  };

  const openLeadDetail = async (lead) => {
    try {
      const response = await axiosInstance.get(`/leads/marketplace/${lead._id}`);
      if (response.data.success) {
        setSelectedLead(response.data.lead);
        setShowDetailModal(true);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load lead details", variant: "destructive" });
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHrs < 1) return 'Just now';
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  // Access denied screen
  if (authChecking) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#2B4C6F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Payment verification screen
  if (verifyingPayment) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#000] p-8 text-center max-w-md">
          <div className="w-16 h-16 border-4 border-[#2B4C6F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-black uppercase mb-2">Verifying Payment</h2>
          <p className="text-neutral-600 dark:text-neutral-400">
            Please wait while we confirm your purchase...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#000] p-8 text-center">
          <Lock size={64} weight="bold" className="mx-auto mb-4 text-[#2B4C6F]" />
          <h1 className="text-2xl font-black uppercase mb-2">Contractor Access Only</h1>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            The Leads Marketplace is exclusively for registered contractors.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/login')} className="w-full bg-[#2B4C6F] border-2 border-black rounded-none">
              Sign In as Contractor
            </Button>
            <Button onClick={() => navigate('/signup?type=contractor')} variant="outline" className="w-full border-2 border-black rounded-none">
              Create Contractor Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]">
      {/* Header */}
      <div className="bg-[#0F172A] border-b-4 border-black py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Top row - Back button and title */}
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate('/contractor-dashboard')}
              className="border-2 border-white text-white hover:bg-white/10 rounded-none px-3"
              size="sm"
            >
              <ArrowLeft size={18} weight="bold" />
              <span className="hidden sm:inline ml-2">Dashboard</span>
            </Button>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D95800] border-2 border-white flex items-center justify-center flex-shrink-0">
                <ShoppingCart size={24} weight="bold" className="text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-white uppercase leading-tight">
                  Leads Marketplace
                </h1>
                <p className="text-neutral-400 text-xs sm:text-sm hidden sm:block">Find qualified leads in your area</p>
              </div>
            </div>
          </div>
          
          {/* Credits & Alerts Row - Responsive */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* Lead Alerts Button */}
            <Button
              onClick={() => setShowAlertsModal(true)}
              size="sm"
              data-testid="open-lead-alerts-modal"
              className={`border-2 border-white rounded-none text-xs sm:text-sm ${
                alertPrefs.enabled 
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                  : 'bg-transparent hover:bg-white/10 text-white'
              }`}
            >
              {alertPrefs.enabled ? (
                <BellRinging size={16} weight="bold" className="mr-1" />
              ) : (
                <Bell size={16} weight="bold" className="mr-1" />
              )}
              <span className="hidden xs:inline">Alerts</span> {alertPrefs.enabled ? 'On' : 'Off'}
            </Button>
            
            {/* Credit Balance */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 border-2 border-white px-3 py-1.5 flex items-center gap-1.5">
              <Coins size={16} weight="bold" className="text-white" />
              <span className="text-white font-bold text-sm">{creditBalance}</span>
              <span className="text-white/80 text-xs hidden sm:inline">Credits</span>
            </div>
            
            {/* Buy Credits Button */}
            <Button
              onClick={() => setShowCreditsModal(true)}
              size="sm"
              className="bg-emerald-500 hover:bg-emerald-600 border-2 border-white text-white rounded-none text-xs sm:text-sm"
            >
              <Plus size={16} weight="bold" className="mr-1" />
              <span className="hidden sm:inline">Buy</span> Credits
            </Button>
          </div>
          
          {/* Trust badges - Hidden on mobile */}
          <div className="hidden sm:flex flex-wrap gap-4">
            <div className="flex items-center gap-2 text-white/80">
              <ShieldCheck size={18} weight="bold" className="text-emerald-400" />
              <span className="text-sm">Verified Homeowners</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Lightning size={18} weight="bold" className="text-amber-400" />
              <span className="text-sm">Real-Time Leads</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Star size={18} weight="bold" className="text-[#2B4C6F]" />
              <span className="text-sm">Exclusive Contact Info</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Coins size={18} weight="bold" className="text-amber-400" />
              <span className="text-sm">Save 40% with Credits</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setShowPurchasedTab(false); setCurrentPage(0); }}
            className={`px-6 py-3 font-bold uppercase tracking-wider border-2 border-black transition-all ${
              !showPurchasedTab 
                ? 'bg-[#2B4C6F] text-white shadow-[4px_4px_0px_#000]' 
                : 'bg-white dark:bg-neutral-800 hover:bg-neutral-100'
            }`}
          >
            <ShoppingCart size={18} weight="bold" className="inline mr-2" />
            Browse Leads ({total})
          </button>
          <button
            onClick={() => { setShowPurchasedTab(true); setCurrentPage(0); }}
            className={`px-6 py-3 font-bold uppercase tracking-wider border-2 border-black transition-all ${
              showPurchasedTab 
                ? 'bg-[#2B4C6F] text-white shadow-[4px_4px_0px_#000]' 
                : 'bg-white dark:bg-neutral-800 hover:bg-neutral-100'
            }`}
          >
            <Receipt size={18} weight="bold" className="inline mr-2" />
            My Purchases ({purchasedLeads.length})
          </button>
        </div>

        {/* Filters (Browse tab only) */}
        {!showPurchasedTab && (
          <div className="bg-white dark:bg-[#0F172A] border-2 border-black shadow-[4px_4px_0px_#000] mb-6">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full p-4 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              <span className="flex items-center gap-2 font-bold uppercase tracking-wider">
                <Funnel size={18} weight="bold" />
                Filter Leads
              </span>
              <CaretDown size={18} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            
            {showFilters && (
              <div className="p-4 border-t-2 border-black dark:border-white grid md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">Job Type</Label>
                  <select
                    value={filterJobType}
                    onChange={(e) => { setFilterJobType(e.target.value); setCurrentPage(0); }}
                    className="w-full h-10 px-3 border-2 border-black dark:border-white bg-white dark:bg-neutral-900 rounded-none"
                  >
                    <option value="">All Types</option>
                    {Object.entries(jobTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">State</Label>
                  <Input
                    value={filterState}
                    onChange={(e) => setFilterState(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="TX, CA, FL..."
                    className="border-2 border-black dark:border-white rounded-none"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">Zip Code</Label>
                  <Input
                    value={filterZip}
                    onChange={(e) => setFilterZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="78701"
                    className="border-2 border-black dark:border-white rounded-none"
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">Timeline</Label>
                  <select
                    value={filterTimeline}
                    onChange={(e) => { setFilterTimeline(e.target.value); setCurrentPage(0); }}
                    className="w-full h-10 px-3 border-2 border-black dark:border-white bg-white dark:bg-neutral-900 rounded-none"
                  >
                    <option value="">Any Timeline</option>
                    <option value="asap">ASAP / Urgent</option>
                    <option value="1-2_weeks">Within 1-2 weeks</option>
                    <option value="1_month">Within a month</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                <div className="md:col-span-4 flex justify-end pt-1">
                  <button
                    onClick={saveCurrentSearchAsAlert}
                    disabled={!filterJobType && !filterState && !filterZip && !filterTimeline}
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#D95800] hover:bg-[#B84A00] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm uppercase tracking-wide border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
                    data-testid="save-search-alert-btn"
                    title={(!filterJobType && !filterState && !filterZip && !filterTimeline) ? 'Set at least one filter first' : 'Get notified when new leads match these filters'}
                  >
                    <BellRinging size={16} weight="bold" />
                    Save This Search as Alert
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#2B4C6F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : showPurchasedTab ? (
          /* Purchased Leads */
          <div className="space-y-4">
            {purchasedLeads.length === 0 ? (
              <div className="bg-white dark:bg-[#0F172A] border-2 border-black p-12 text-center">
                <Receipt size={48} className="mx-auto mb-4 text-neutral-400" />
                <p className="text-neutral-500 mb-4">You haven't purchased any leads yet.</p>
                <Button onClick={() => setShowPurchasedTab(false)} className="bg-[#2B4C6F] border-2 border-black rounded-none">
                  Browse Available Leads
                </Button>
              </div>
            ) : (
              purchasedLeads.map((purchase) => {
                const IconComponent = jobTypeIcons[purchase.lead.job_type] || HardHat;
                return (
                  <div key={purchase.purchase_id} className="bg-white dark:bg-[#0F172A] border-2 border-black shadow-[4px_4px_0px_#000] p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-emerald-500 border-2 border-black flex items-center justify-center flex-shrink-0">
                          <IconComponent size={24} weight="bold" className="text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-lg">{jobTypeLabels[purchase.lead.job_type]}</h3>
                            <Badge className="bg-emerald-500 text-white border-0">Purchased</Badge>
                          </div>
                          <p className="text-sm text-neutral-500 mb-2">
                            <MapPin size={14} className="inline mr-1" />
                            {purchase.lead.location?.city}, {purchase.lead.location?.state} {purchase.lead.location?.zip_code}
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <a href={`tel:${purchase.lead.contact?.phone}`} className="flex items-center gap-1 text-[#2B4C6F] hover:underline">
                              <Phone size={16} weight="bold" />
                              {purchase.lead.contact?.phone}
                            </a>
                            <a href={`mailto:${purchase.lead.contact?.email}`} className="flex items-center gap-1 text-[#2B4C6F] hover:underline">
                              <Envelope size={16} weight="bold" />
                              {purchase.lead.contact?.email}
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-neutral-500">Purchased {formatDate(purchase.purchased_at)}</p>
                        <p className="text-sm font-bold text-emerald-600">${purchase.price} paid</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2">{purchase.lead.description}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          /* Available Leads Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {leads.length === 0 ? (
              <div className="col-span-full bg-white dark:bg-[#0F172A] border-2 border-black p-12 text-center">
                <MagnifyingGlass size={48} className="mx-auto mb-4 text-neutral-400" />
                <p className="text-neutral-500">No leads found matching your filters.</p>
              </div>
            ) : (
              leads.map((lead) => {
                const IconComponent = jobTypeIcons[lead.job_type] || HardHat;
                return (
                  <div 
                    key={lead._id} 
                    className={`bg-white dark:bg-[#0F172A] border-2 border-black shadow-[4px_4px_0px_#000] overflow-hidden ${lead.is_purchased ? 'border-emerald-500' : ''}`}
                  >
                    {/* Card Header */}
                    <div className={`p-4 border-b-2 border-black flex items-center justify-between ${lead.is_purchased ? 'bg-emerald-500' : 'bg-[#2B4C6F]'}`}>
                      <div className="flex items-center gap-2">
                        <IconComponent size={20} weight="bold" className="text-white" />
                        <span className="font-bold text-white text-sm uppercase tracking-wider">
                          {jobTypeLabels[lead.job_type]}
                        </span>
                      </div>
                      {lead.is_purchased ? (
                        <Badge className="bg-white text-emerald-600 border-0 font-bold">
                          <CheckCircle size={14} weight="bold" className="mr-1" />
                          Unlocked
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          {lead.base_price && lead.price !== lead.base_price && (
                            <span className="text-white/60 text-xs line-through">${lead.base_price}</span>
                          )}
                          <Badge className={`border-0 font-bold ${
                            lead.lead_grade === 'A' || lead.lead_grade === 'B' 
                              ? 'bg-amber-100 text-amber-800' 
                              : lead.lead_grade === 'D' || lead.lead_grade === 'F'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-white text-[#2B4C6F]'
                          }`} data-testid={`lead-price-${lead._id}`}>
                            ${lead.price}
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    {/* Card Body */}
                    <div className="p-4">
                      {!lead.is_purchased && (lead.view_count || 0) >= 3 && (
                        <div className="mb-2" data-testid={`lead-going-fast-${lead._id}`}>
                          <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-1 border border-black">
                            <Fire size={12} weight="fill" /> {lead.view_count} contractors viewing
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                        <MapPin size={16} weight="bold" />
                        <span>{lead.location?.city}, {lead.location?.state} {lead.location?.zip_code}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 mb-3">
                        <Clock size={16} weight="bold" />
                        <span>{timelineLabels[lead.timeline] || lead.timeline}</span>
                        {lead.lead_score != null && (
                          <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-xs font-black rounded border-2 ${
                            lead.lead_grade === 'A' ? 'bg-emerald-100 text-emerald-700 border-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700' :
                            lead.lead_grade === 'B' ? 'bg-blue-100 text-blue-700 border-blue-400 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700' :
                            lead.lead_grade === 'C' ? 'bg-amber-100 text-amber-700 border-amber-400 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700' :
                            'bg-neutral-100 text-neutral-600 border-neutral-400 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-600'
                          }`} data-testid={`lead-score-${lead._id}`}>
                            <TrendUp size={10} weight="bold" /> {lead.lead_score} / {lead.lead_grade}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 line-clamp-2 mb-4">
                        {lead.description}
                      </p>
                      
                      {/* Contact Preview */}
                      <div className="p-3 bg-neutral-100 dark:bg-neutral-800 border-2 border-dashed border-neutral-300 dark:border-neutral-600 mb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <User size={16} className="text-neutral-500" />
                          <span className={`text-sm ${lead.is_purchased ? 'font-bold' : 'text-neutral-500'}`}>
                            {lead.contact?.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                          <Phone size={16} className="text-neutral-500" />
                          <span className={`text-sm ${lead.is_purchased ? 'text-[#2B4C6F]' : 'text-neutral-400'}`}>
                            {lead.is_purchased ? (
                              <a href={`tel:${lead.contact?.phone}`} className="hover:underline">{lead.contact?.phone}</a>
                            ) : lead.contact?.phone}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Envelope size={16} className="text-neutral-500" />
                          <span className={`text-sm ${lead.is_purchased ? 'text-[#2B4C6F]' : 'text-neutral-400'}`}>
                            {lead.is_purchased ? (
                              <a href={`mailto:${lead.contact?.email}`} className="hover:underline">{lead.contact?.email}</a>
                            ) : lead.contact?.email}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => openLeadDetail(lead)}
                          className="flex-1 border-2 border-black dark:border-white rounded-none"
                        >
                          <Eye size={16} weight="bold" className="mr-1" />
                          Details
                        </Button>
                        {lead.is_purchased ? (
                          <Button disabled className="flex-1 bg-emerald-500 border-2 border-black rounded-none cursor-default">
                            <CheckCircle size={16} weight="bold" className="mr-1" />
                            Purchased
                          </Button>
                        ) : (
                          <div className="flex-1 flex flex-col gap-1">
                            {creditBalance >= lead.price ? (
                              <Button
                                onClick={() => purchaseLeadWithCredits(lead._id, lead.price)}
                                disabled={purchasing || verifyingPayment}
                                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 border-2 border-black rounded-none text-sm"
                              >
                                {purchasing ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <>
                                    <Coins size={14} weight="bold" className="mr-1" />
                                    Use {lead.price} Credits
                                  </>
                                )}
                              </Button>
                            ) : (
                              <Button
                                onClick={() => purchaseLead(lead._id)}
                                disabled={purchasing || verifyingPayment}
                                className="w-full bg-[#D95800] hover:bg-[#E55D00] border-2 border-black rounded-none text-sm"
                              >
                                {purchasing ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <>
                                    <CreditCard size={14} weight="bold" className="mr-1" />
                                    Pay ${lead.price}
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Time ago */}
                      <p className="text-xs text-neutral-400 text-center mt-3">
                        Posted {getTimeAgo(lead.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Pagination (Browse tab only) */}
        {!showPurchasedTab && total > limit && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-neutral-500">
              Showing {currentPage * limit + 1} - {Math.min((currentPage + 1) * limit, total)} of {total} leads
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="border-2 border-black rounded-none"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                disabled={(currentPage + 1) * limit >= total}
                onClick={() => setCurrentPage(currentPage + 1)}
                className="border-2 border-black rounded-none"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#000] w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className={`p-4 border-b-2 border-black flex items-center justify-between ${selectedLead.is_purchased ? 'bg-emerald-500' : 'bg-[#2B4C6F]'}`}>
              <h2 className="text-xl font-black text-white uppercase">
                {jobTypeLabels[selectedLead.job_type]}
              </h2>
              <button onClick={() => setShowDetailModal(false)} className="text-white hover:text-neutral-200">
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Location */}
              <div className="flex items-center gap-2">
                <MapPin size={20} weight="bold" className="text-[#2B4C6F]" />
                <span className="font-bold">
                  {selectedLead.location?.city}, {selectedLead.location?.state} {selectedLead.location?.zip_code}
                </span>
              </div>

              {/* Timeline */}
              <div className="flex items-center gap-2">
                <Clock size={20} weight="bold" className="text-[#2B4C6F]" />
                <span>{timelineLabels[selectedLead.timeline] || selectedLead.timeline}</span>
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Project Description</Label>
                <div className="p-4 bg-neutral-100 dark:bg-neutral-800 border-2 border-black">
                  <p className="text-neutral-700 dark:text-neutral-300">{selectedLead.description}</p>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Contact Information</Label>
                <div className={`p-4 border-2 ${selectedLead.is_purchased ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500' : 'bg-neutral-100 dark:bg-neutral-800 border-dashed border-neutral-400'}`}>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User size={18} className="text-neutral-500" />
                      <span className={selectedLead.is_purchased ? 'font-bold' : 'text-neutral-500'}>{selectedLead.contact?.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={18} className="text-neutral-500" />
                      {selectedLead.is_purchased ? (
                        <a href={`tel:${selectedLead.contact?.phone}`} className="text-[#2B4C6F] font-bold hover:underline">
                          {selectedLead.contact?.phone}
                        </a>
                      ) : (
                        <span className="text-neutral-400">{selectedLead.contact?.phone}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Envelope size={18} className="text-neutral-500" />
                      {selectedLead.is_purchased ? (
                        <a href={`mailto:${selectedLead.contact?.email}`} className="text-[#2B4C6F] font-bold hover:underline">
                          {selectedLead.contact?.email}
                        </a>
                      ) : (
                        <span className="text-neutral-400">{selectedLead.contact?.email}</span>
                      )}
                    </div>
                  </div>
                  
                  {!selectedLead.is_purchased && (
                    <div className="mt-3 pt-3 border-t border-neutral-300 dark:border-neutral-600 flex items-center gap-2 text-amber-600">
                      <Lock size={16} weight="bold" />
                      <span className="text-sm">Purchase to unlock full contact info</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Price / Purchase */}
              <div className="pt-4 border-t-2 border-neutral-200 dark:border-neutral-700">
                {selectedLead.is_purchased ? (
                  <div className="bg-emerald-100 dark:bg-emerald-900/30 border-2 border-emerald-500 p-4 text-center">
                    <CheckCircle size={32} weight="bold" className="mx-auto mb-2 text-emerald-600" />
                    <p className="font-bold text-emerald-700 dark:text-emerald-300">You own this lead</p>
                    <p className="text-sm text-emerald-600">Purchased on {formatDate(selectedLead.purchase_date)}</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-3xl font-black text-[#D95800] mb-1">${selectedLead.price}</p>
                    {selectedLead.base_price && selectedLead.price !== selectedLead.base_price && (
                      <p className="text-xs mb-1">
                        <span className="line-through text-neutral-400">${selectedLead.base_price}</span>
                        <span className={`ml-1 font-bold ${selectedLead.price > selectedLead.base_price ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {selectedLead.price > selectedLead.base_price ? 'Premium' : 'Discount'} (Grade {selectedLead.lead_grade})
                        </span>
                      </p>
                    )}
                    <p className="text-sm text-neutral-500 mb-4">
                      {creditBalance >= selectedLead.price 
                        ? `You have ${creditBalance} credits available` 
                        : "One-time purchase"}
                    </p>

                    {/* Referral credits toggle */}
                    {referralCredits > 0 && (
                      <button
                        type="button"
                        onClick={() => setUseReferralCredits(v => !v)}
                        className={`w-full mb-3 p-3 border-2 border-black rounded-none transition-all flex items-center gap-3 text-left ${
                          useReferralCredits
                            ? 'bg-[#D95800]/10 dark:bg-[#D95800]/20'
                            : 'bg-white dark:bg-neutral-800'
                        }`}
                        data-testid="apply-referral-credits-toggle"
                      >
                        <div className={`w-5 h-5 border-2 border-black flex items-center justify-center flex-shrink-0 ${useReferralCredits ? 'bg-[#D95800]' : 'bg-white'}`}>
                          {useReferralCredits && <Check size={14} weight="bold" className="text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-black text-neutral-900 dark:text-white">
                            Apply ${Math.min(referralCredits, selectedLead.price)} in referral credits
                          </p>
                          <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                            You have ${referralCredits} from referrals.
                            {useReferralCredits && referralCredits >= selectedLead.price
                              ? ' Lead is FREE for you!'
                              : useReferralCredits
                                ? ` You'll only pay ${Math.max(0, selectedLead.price - referralCredits)} credits.`
                                : ''}
                          </p>
                        </div>
                      </button>
                    )}

                    {/* Credit payment option */}
                    {(creditBalance + (useReferralCredits ? referralCredits : 0)) >= selectedLead.price && (
                      <Button
                        onClick={() => purchaseLeadWithCredits(selectedLead._id, selectedLead.price)}
                        disabled={purchasing || verifyingPayment}
                        className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] rounded-none mb-3"
                        data-testid="purchase-with-credits-btn"
                      >
                        {purchasing ? (
                          <span className="flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Processing...
                          </span>
                        ) : (() => {
                          const refUsed = useReferralCredits ? Math.min(referralCredits, selectedLead.price) : 0;
                          const creditUsed = Math.max(0, selectedLead.price - refUsed);
                          return (
                            <>
                              <Coins size={20} weight="bold" className="mr-2" />
                              {refUsed > 0 && creditUsed > 0
                                ? `Use $${refUsed} Referral + ${creditUsed} Credits`
                                : refUsed > 0
                                  ? `Use $${refUsed} Referral Credits (FREE)`
                                  : `Use ${selectedLead.price} Credits`}
                            </>
                          );
                        })()}
                      </Button>
                    )}
                    
                    {/* Stripe payment option */}
                    <Button
                      onClick={() => purchaseLead(selectedLead._id)}
                      disabled={purchasing || verifyingPayment}
                      variant={creditBalance >= selectedLead.price ? "outline" : "default"}
                      className={`w-full h-12 font-bold uppercase tracking-wider border-2 border-black rounded-none ${
                        creditBalance >= selectedLead.price 
                          ? 'bg-white hover:bg-neutral-100 text-neutral-800' 
                          : 'bg-[#D95800] hover:bg-[#E55D00] text-white shadow-[4px_4px_0px_#000]'
                      }`}
                    >
                      {purchasing ? (
                        <span className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                          Redirecting...
                        </span>
                      ) : (
                        <>
                          <CreditCard size={20} weight="bold" className="mr-2" />
                          Pay ${selectedLead.price} with Card
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-neutral-400 mt-2 flex items-center justify-center gap-1">
                      <ShieldCheck size={14} weight="bold" />
                      Secure payment powered by Stripe
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credits Purchase Modal */}
      {showCreditsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#000] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 border-b-2 border-black flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Coins size={28} weight="bold" className="text-white" />
                <div>
                  <h2 className="text-xl font-black text-white uppercase">Buy Lead Credits</h2>
                  <p className="text-white/80 text-sm">Save up to 40% with bulk purchases</p>
                </div>
              </div>
              <button onClick={() => setShowCreditsModal(false)} className="text-white hover:text-neutral-200">
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Current Balance */}
              <div className="bg-neutral-100 dark:bg-neutral-800 border-2 border-black p-4 mb-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-500 uppercase tracking-wider">Current Balance</p>
                  <p className="text-3xl font-black">{creditBalance} <span className="text-lg">Credits</span></p>
                </div>
                <Wallet size={40} weight="bold" className="text-[#2B4C6F]" />
              </div>

              {/* Credit Packages */}
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(creditPackages).map(([id, pkg]) => (
                  <div 
                    key={id} 
                    className={`border-2 border-black p-4 relative ${
                      id === 'popular' ? 'bg-amber-50 dark:bg-amber-900/20 shadow-[4px_4px_0px_#F59E0B]' : ''
                    }`}
                  >
                    {id === 'popular' && (
                      <div className="absolute -top-3 -right-2 bg-amber-500 text-white text-xs font-bold px-2 py-1 border-2 border-black">
                        BEST VALUE
                      </div>
                    )}
                    
                    <h3 className="font-bold text-lg mb-1">{pkg.name}</h3>
                    <p className="text-sm text-neutral-500 mb-3">{pkg.description}</p>
                    
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-3xl font-black">${pkg.price}</span>
                      <span className="text-neutral-500">→</span>
                      <span className="text-2xl font-bold text-emerald-600">{pkg.credits} Credits</span>
                    </div>
                    
                    {pkg.bonus > 0 && (
                      <div className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm font-bold px-2 py-1 inline-flex items-center gap-1 mb-3">
                        <Gift size={14} weight="bold" />
                        +{pkg.bonus} Bonus Credits!
                      </div>
                    )}
                    
                    <Button
                      onClick={() => purchaseCredits(id)}
                      disabled={purchasingCredits}
                      className={`w-full border-2 border-black rounded-none ${
                        id === 'popular' 
                          ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                          : 'bg-[#2B4C6F] hover:bg-[#203A55] text-white'
                      }`}
                    >
                      {purchasingCredits ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          Buy for ${pkg.price}
                          <ArrowRight size={16} weight="bold" className="ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>

              {/* Auto Top-Up */}
              <div className="mt-6 border-2 border-black p-4 bg-emerald-50 dark:bg-emerald-900/10" data-testid="auto-topup-section">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-black uppercase flex items-center gap-2">
                    <Lightning size={18} weight="bold" className="text-emerald-600" />
                    Auto Top-Up
                  </h4>
                  {autoTopup?.enabled && (
                    <span className="text-xs font-bold bg-emerald-600 text-white px-2 py-1 border-2 border-black" data-testid="auto-topup-status-on">ON</span>
                  )}
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">
                  {autoTopup?.mode === 'notify'
                    ? "Never miss a hot lead. The moment your balance drops below your threshold, we instantly email you a one-click refill link. Automatic card charging unlocks once live payments are connected."
                    : "Never miss a hot lead. When your balance drops below your threshold, we automatically charge your saved card and refill your credits."}
                </p>

                {autoTopup?.mode === 'notify' ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold uppercase text-neutral-500">Alert when below</label>
                        <select
                          value={atThreshold}
                          onChange={(e) => setAtThreshold(parseInt(e.target.value, 10))}
                          disabled={atSaving}
                          className="w-full border-2 border-black p-2 bg-white dark:bg-neutral-800 text-sm font-bold"
                          data-testid="auto-topup-threshold-select"
                        >
                          {[10, 25, 50, 100].map(t => <option key={t} value={t}>{t} credits</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase text-neutral-500">Suggested refill</label>
                        <select
                          value={atPackage}
                          onChange={(e) => setAtPackage(e.target.value)}
                          disabled={atSaving}
                          className="w-full border-2 border-black p-2 bg-white dark:bg-neutral-800 text-sm font-bold"
                          data-testid="auto-topup-package-select"
                        >
                          {Object.entries(creditPackages).map(([id, pkg]) => (
                            <option key={id} value={id}>{pkg.name} (${pkg.price} → {pkg.credits})</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {autoTopup?.enabled ? (
                      <div className="flex gap-3">
                        <Button
                          onClick={enableSmartRefill}
                          disabled={atSaving}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-black rounded-none"
                          data-testid="auto-topup-save-btn"
                        >
                          Save Settings
                        </Button>
                        <Button
                          onClick={() => updateAutoTopup({ enabled: false })}
                          disabled={atSaving}
                          className="flex-1 bg-neutral-200 hover:bg-neutral-300 text-black border-2 border-black rounded-none"
                          data-testid="auto-topup-toggle-btn"
                        >
                          Pause Smart Refill
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={enableSmartRefill}
                        disabled={atSaving}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-black rounded-none"
                        data-testid="auto-topup-enable-btn"
                      >
                        {atSaving ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Lightning size={16} weight="bold" className="mr-2" />
                            Enable Smart Refill
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ) : autoTopup?.has_card ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm bg-white dark:bg-neutral-800 border-2 border-black p-2">
                      <span className="font-bold flex items-center gap-2">
                        <CreditCard size={16} weight="bold" />
                        {(autoTopup.card_brand || 'card').toUpperCase()} •••• {autoTopup.card_last4}
                      </span>
                      <button onClick={removeAutoTopup} disabled={atSaving} className="text-red-600 font-bold underline text-xs" data-testid="auto-topup-remove-btn">
                        Remove card
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold uppercase text-neutral-500">Refill when below</label>
                        <select
                          value={autoTopup.threshold}
                          onChange={(e) => updateAutoTopup({ threshold: parseInt(e.target.value, 10) })}
                          disabled={atSaving}
                          className="w-full border-2 border-black p-2 bg-white dark:bg-neutral-800 text-sm font-bold"
                          data-testid="auto-topup-threshold-select"
                        >
                          {[10, 25, 50, 100].map(t => <option key={t} value={t}>{t} credits</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase text-neutral-500">Refill with</label>
                        <select
                          value={autoTopup.package_id}
                          onChange={(e) => updateAutoTopup({ package_id: e.target.value })}
                          disabled={atSaving}
                          className="w-full border-2 border-black p-2 bg-white dark:bg-neutral-800 text-sm font-bold"
                          data-testid="auto-topup-package-select"
                        >
                          {Object.entries(creditPackages).map(([id, pkg]) => (
                            <option key={id} value={id}>{pkg.name} (${pkg.price} → {pkg.credits})</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <Button
                      onClick={() => updateAutoTopup({ enabled: !autoTopup.enabled })}
                      disabled={atSaving}
                      className={`w-full border-2 border-black rounded-none ${autoTopup.enabled ? 'bg-neutral-200 hover:bg-neutral-300 text-black' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                      data-testid="auto-topup-toggle-btn"
                    >
                      {autoTopup.enabled ? 'Pause Auto Top-Up' : 'Resume Auto Top-Up'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold uppercase text-neutral-500">Refill when below</label>
                        <select
                          value={atThreshold}
                          onChange={(e) => setAtThreshold(parseInt(e.target.value, 10))}
                          className="w-full border-2 border-black p-2 bg-white dark:bg-neutral-800 text-sm font-bold"
                          data-testid="auto-topup-threshold-select"
                        >
                          {[10, 25, 50, 100].map(t => <option key={t} value={t}>{t} credits</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase text-neutral-500">Refill with</label>
                        <select
                          value={atPackage}
                          onChange={(e) => setAtPackage(e.target.value)}
                          className="w-full border-2 border-black p-2 bg-white dark:bg-neutral-800 text-sm font-bold"
                          data-testid="auto-topup-package-select"
                        >
                          {Object.entries(creditPackages).map(([id, pkg]) => (
                            <option key={id} value={id}>{pkg.name} (${pkg.price} → {pkg.credits})</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <Button
                      onClick={startAutoTopupSetup}
                      disabled={atSaving}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-black rounded-none"
                      data-testid="auto-topup-enable-btn"
                    >
                      {atSaving ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <CreditCard size={16} weight="bold" className="mr-2" />
                          Save Card & Enable Auto Top-Up
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-[#2B4C6F]">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  <TrendUp size={18} weight="bold" className="text-[#2B4C6F]" />
                  Why Buy Credits?
                </h4>
                <ul className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                  <li>• <strong>Save money</strong> - Up to 40% discount on bulk purchases</li>
                  <li>• <strong>Instant unlock</strong> - No checkout redirect, unlock leads immediately</li>
                  <li>• <strong>Never expires</strong> - Credits stay in your account forever</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Alerts Modal */}
      {showAlertsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#000] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-[#2B4C6F] p-4 border-b-2 border-black flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BellRinging size={28} weight="bold" className="text-white" />
                <div>
                  <h2 className="text-xl font-black text-white uppercase">Lead Alerts</h2>
                  <p className="text-white/80 text-sm">Get notified when matching leads are posted</p>
                </div>
              </div>
              <button onClick={() => setShowAlertsModal(false)} className="text-white hover:text-neutral-200">
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {alertsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-[#2B4C6F] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  {/* Enable/Disable Toggle */}
                  <div className="bg-neutral-100 dark:bg-neutral-800 border-2 border-black p-4 mb-6 flex items-center justify-between">
                    <div>
                      <p className="font-bold text-lg">Enable Lead Alerts</p>
                      <p className="text-sm text-neutral-500">Receive notifications for new leads</p>
                    </div>
                    <button
                      onClick={() => setAlertPrefs(prev => ({ ...prev, enabled: !prev.enabled }))}
                      className={`w-14 h-8 rounded-full border-2 border-black relative transition-colors ${
                        alertPrefs.enabled ? 'bg-emerald-500' : 'bg-neutral-300'
                      }`}
                    >
                      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full border border-black transition-transform ${
                        alertPrefs.enabled ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {alertPrefs.enabled && (
                    <>
                      {/* Job Types */}
                      <div className="mb-6">
                        <h3 className="font-bold mb-3 flex items-center gap-2">
                          <Hammer size={18} weight="bold" />
                          Job Types (Select all that apply)
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {Object.keys(jobTypeIcons).map(jobType => (
                            <button
                              key={jobType}
                              onClick={() => toggleJobTypeAlert(jobType)}
                              className={`p-2 text-sm border-2 border-black rounded-none transition-colors ${
                                alertPrefs.job_types.includes(jobType)
                                  ? 'bg-[#2B4C6F] text-white'
                                  : 'bg-white dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                              }`}
                            >
                              {alertPrefs.job_types.includes(jobType) && <Check size={14} weight="bold" className="inline mr-1" />}
                              {jobType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-neutral-500 mt-2">Leave empty to receive alerts for all job types</p>
                      </div>

                      {/* States */}
                      <div className="mb-6">
                        <h3 className="font-bold mb-3 flex items-center gap-2">
                          <MapPin size={18} weight="bold" />
                          States (Select your service areas)
                        </h3>
                        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                          {['CA', 'TX', 'FL', 'NY', 'PA', 'IL', 'OH', 'GA', 'NC', 'MI', 'AZ', 'WA'].map(state => (
                            <button
                              key={state}
                              onClick={() => toggleStateAlert(state)}
                              className={`p-2 text-sm border-2 border-black rounded-none transition-colors ${
                                alertPrefs.states.includes(state)
                                  ? 'bg-[#2B4C6F] text-white'
                                  : 'bg-white dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                              }`}
                            >
                              {alertPrefs.states.includes(state) && <Check size={12} weight="bold" className="inline mr-1" />}
                              {state}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-neutral-500 mt-2">Leave empty to receive alerts for all states</p>
                      </div>

                      {/* Timeline */}
                      <div className="mb-6">
                        <h3 className="font-bold mb-3 flex items-center gap-2">
                          <Clock size={18} weight="bold" />
                          Project Timeline
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {[
                            { value: 'asap', label: 'ASAP' },
                            { value: '1_2_weeks', label: '1-2 Weeks' },
                            { value: '1_month', label: '1 Month' },
                            { value: 'flexible', label: 'Flexible' }
                          ].map(({ value, label }) => (
                            <button
                              key={value}
                              onClick={() => toggleTimelineAlert(value)}
                              className={`p-2 text-sm border-2 border-black rounded-none transition-colors ${
                                alertPrefs.timelines.includes(value)
                                  ? 'bg-[#2B4C6F] text-white'
                                  : 'bg-white dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                              }`}
                            >
                              {alertPrefs.timelines.includes(value) && <Check size={12} weight="bold" className="inline mr-1" />}
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Alert Frequency */}
                      <div className="mb-6">
                        <h3 className="font-bold mb-3 flex items-center gap-2">
                          <Clock size={18} weight="bold" />
                          Email Frequency
                        </h3>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: 'instant', label: 'Instant', hint: 'As leads arrive' },
                            { value: 'daily', label: 'Daily Digest', hint: 'One email a day' },
                            { value: 'weekly', label: 'Weekly Digest', hint: 'One email a week' },
                          ].map(({ value, label, hint }) => (
                            <button
                              key={value}
                              onClick={() => setAlertPrefs((p) => ({ ...p, frequency: value }))}
                              className={`p-3 border-2 border-black rounded-none transition-colors text-center ${
                                alertPrefs.frequency === value
                                  ? 'bg-[#D95800] text-white'
                                  : 'bg-white dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                              }`}
                              data-testid={`alert-frequency-${value}`}
                            >
                              <p className="font-bold text-sm">{alertPrefs.frequency === value && <Check size={14} weight="bold" className="inline mr-1" />}{label}</p>
                              <p className="text-[11px] opacity-70">{hint}</p>
                            </button>
                          ))}
                        </div>
                        {alertPrefs.frequency !== 'instant' && (
                          <p className="text-[11px] text-neutral-500 mt-2">
                            Digests bundle all new matching leads into a single email — only sent when there's something new.
                          </p>
                        )}
                      </div>

                      {/* Notification Channels */}
                      <div className="mb-6">
                        <h3 className="font-bold mb-3 flex items-center gap-2">
                          <Bell size={18} weight="bold" />
                          Notification Channels
                        </h3>
                        <div className="flex gap-4">
                          <button
                            onClick={() => toggleChannelAlert('in_app')}
                            className={`flex-1 p-3 border-2 border-black rounded-none transition-colors ${
                              alertPrefs.notification_channels.includes('in_app')
                                ? 'bg-emerald-500 text-white'
                                : 'bg-white dark:bg-neutral-800'
                            }`}
                            data-testid="alert-channel-in-app"
                          >
                            <Bell size={24} weight="bold" className="mx-auto mb-1" />
                            <p className="font-bold">In-App</p>
                            <p className="text-xs opacity-70">Bell notifications</p>
                          </button>
                          <button
                            onClick={() => toggleChannelAlert('email')}
                            className={`flex-1 p-3 border-2 border-black rounded-none transition-colors ${
                              alertPrefs.notification_channels.includes('email')
                                ? 'bg-emerald-500 text-white'
                                : 'bg-white dark:bg-neutral-800'
                            }`}
                            data-testid="alert-channel-email"
                          >
                            <Envelope size={24} weight="bold" className="mx-auto mb-1" />
                            <p className="font-bold">Email</p>
                            <p className="text-xs opacity-70">Instant emails</p>
                          </button>
                          <button
                            onClick={() => toggleChannelAlert('sms')}
                            className={`flex-1 p-3 border-2 border-black rounded-none transition-colors ${
                              alertPrefs.notification_channels.includes('sms')
                                ? 'bg-emerald-500 text-white'
                                : 'bg-white dark:bg-neutral-800'
                            }`}
                            data-testid="alert-channel-sms"
                          >
                            <DeviceMobile size={24} weight="bold" className="mx-auto mb-1" />
                            <p className="font-bold">SMS</p>
                            <p className="text-xs opacity-70">Text alerts</p>
                          </button>
                        </div>
                        {alertPrefs.notification_channels.includes('sms') && (
                          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                            <DeviceMobile size={12} weight="bold" /> Make sure your profile phone number is set — that's where alerts will be sent.
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Save Button */}
                  <Button
                    onClick={() => { saveAlertPreferences(); setShowAlertsModal(false); }}
                    disabled={savingAlerts}
                    className="w-full h-12 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] rounded-none"
                  >
                    {savingAlerts ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check size={20} weight="bold" className="mr-2" />
                        Save Preferences
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
